<template>
  <v-container class="pa-0" :style="appStyle_chartSizing">
    <v-toolbar dense flat dark :color="appColor_Toolbar" rounded>
      <v-toolbar-title :class="appStyle_headingClass">
        {{ getExpression('FormTitle', 'MemoryAlbums') }}
      </v-toolbar-title>
      <v-spacer />

      <!-- Filter -->
      <v-col cols="6">
        <v-select
          v-if="!hideFilter"
          v-model="selectedId"
          :items="memoryAlbums"
          item-value="id"
          item-text="name"
          @change="playlistsChange"
          hide-details
          :disabled="isLoading || disableFilter"
          class="ml-2"
        >
        </v-select>
      </v-col>
    </v-toolbar>

    <!-- Chart -->
    <v-card :loading="isLoading">
      <template v-slot:progress>
        <v-progress-linear
          indeterminate
          :color="appColor_Progress"
        ></v-progress-linear>
      </template>

      <v-row no-gutters>
        <v-col cols="12" sm="12">
          <v-card>
            <!-- memory album description -->
            <v-list-item v-if="isMemoryAlbums">
              <v-list-item-subtitle>
                {{ albumDescription }}
              </v-list-item-subtitle>
            </v-list-item>

            <!-- chart -->
            <v-card-text>
              <template v-if="isMemoryAlbums">
                <template v-if="isPlaylistContent">
                  <doughnut-chart :chart-data="chart"></doughnut-chart>
                </template>
                <template v-else>
                  <span v-html="noContentMessage"></span>
                </template>
              </template>

              <template v-else>
                <span v-html="noMemoryAlbumsMessage"></span>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script>
import appService from '@/app.service.js'
import { mapGetters } from 'vuex'
import { AppColor, DashboardClass, DashboardStyles, LanguageId } from '@/config.js'
import { DoughnutChart } from '@/charts/doughnutChart.js'

export default {
  name: 'PlaylistChart',
  components: {
    DoughnutChart
  },
  props: {
    languageId: {
      type: Number,
      default: LanguageId.English
    },    
    trigger: {
      type: Boolean,
      default: false
    },
    disableFilter: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // colors
      appColor_Progress: AppColor.Progress,
      appColor_Selected: AppColor.Selected,
      appColor_Toolbar: AppColor.Toolbar,

      // styles
      appStyle_headingClass: DashboardClass.Heading,
      appStyle_labelClass: DashboardClass.Label,
      appStyle_valueClass: DashboardClass.Value,
      appStyle_chartSizing: DashboardStyles.ChartSizing,

      selectedId: null,
      chartData: null,
      isPlaylistContent: false,

      isLoading: true
    }
  },
  computed: {
    ...mapGetters(['activeResident']),
    chart () {
      const defaultChart = { id: -1, name: '', description: '' }
      if (this.isNullOrUndefined(this.chartData)) return defaultChart
      if (this.chartData.length === 0) return defaultChart

      const self = this
      const charts = self.chartData.filter(pl => {
        return pl.id === self.selectedId
      })

      return charts.length > 0 ? self.getChart(charts[0]) : null
    },
    noMemoryAlbumsMessage () {
      if (this.isNullOrUndefined(this.chartData)) return ''
      return this.getExpression('Alert', 'NoMemoryAlbums').replace('{0}', this.activeResident.firstName)
    },
    noContentMessage () {
      if (this.isNullOrUndefined(this.chartData)) return ''
      return this.getExpression('Alert', 'NoMemoryAlbumContent').replace('{0}', this.memoryAlbumName)
    },
    albumDescription () {
      if (this.isNullOrUndefined(this.chartData)) return ''
      const self = this
      const charts = self.chartData.filter(pl => {
        return pl.id === self.selectedId
      })
      //TODO use chart() for this
      return charts.length > 0 ? charts[0].description : null
    },
    memoryAlbumName () {
      const self = this
      const memoryAlbums = self.memoryAlbums.filter(pl => pl.id === self.selectedId)
      return memoryAlbums.length > 0
        ? memoryAlbums[0].name
        : ''
    },
    memoryAlbums () {
      if (this.chartData === null) return []
      if (this.chartData.length === 0) return []

      return this.chartData.map(pl => {
        return {
          id: pl.id,
          name: pl.name
        }
      })
    },
    isMemoryAlbums () {
      return this.memoryAlbums.length > 0
    },
    hideFilter () {
      if (this.isNullOrUndefined(this.memoryAlbums)) return true
      return this.memoryAlbums.length <= 1
    }
  },
  methods: {
    loadData () {
      const self = this

      appService.getResidentDashboardPlaylists(self.activeResident.id).then(data => {
        self.isLoading = false
        if (data.errorSeverity > 0) {
          self.handleError(data)
        } else {
          self.chartData = data.playlists
          self.selectedId = self.getActiveId()
          self.isPlaylistContent = self.getSelectedContentCount() > 0
          self.$emit('data-loaded')
        }
      }).catch(e => { self.handleCriticalError(e) })
    },

    getChart (pl) {
      return {
        id: pl.id,
        name: pl.name,
        description: pl.isActive
          ? pl.description + ` (${this.getExpression('Label', 'Active')})`
          : pl.description,
        isActive: pl.isActive,
        datasets: [
          {
            data: [pl.audioCount, pl.imageCount, pl.videoCount],
            backgroundColor: ['pink', 'cyan', 'gold']
          }
        ],
        labels: [
          this.getExpression('MenuItem', 'Audio'),
          this.getExpression('MenuItem', 'Image'),
          this.getExpression('MenuItem', 'Video')]
      }
    },

    getSelectedContentCount () {
      const self = this
      var count = 0
      this.chartData.filter(pl=> pl.id === self.selectedId).forEach(pl => {
        count += (pl.audioCount + pl.imageCount + pl.videoCount)
      })
      return count
    },

    getActiveId () {
      if (this.isNullOrUndefined(this.chartData)) return -1

      const self = this
      const memoryAlbums = self.chartData

      return self.chartData.length > 0 ? memoryAlbums[0].id : -1
    },

    // events
    playlistsChange () {
      this.isPlaylistContent = this.getSelectedContentCount() > 0
    },

    handleError (result) {
      this.$error.show({
        text: result.errorMessage,
        severity: result.errorSeverity,
        languageId: this.languageId
      })
    }
  },
  watch: {
    trigger (val) {
      if (val === null) return
      if (val) this.loadData()
    }
  }
}
</script>
